import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiRequest } from "@/lib/queryClient";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { queryClient } from "@/lib/queryClient";
import { Loader2 } from "lucide-react";

interface WorkspaceGeneralTabProps {
  workspace: any;
}

export function WorkspaceGeneralTab({ workspace }: WorkspaceGeneralTabProps) {
  const [name, setName] = useState(workspace.name || "");
  const [description, setDescription] = useState(workspace.description || "");
  const [saving, setSaving] = useState(false);
  const isDirty = name !== (workspace.name || "") || description !== (workspace.description || "");

  const handleSave = async () => {
    if (!name.trim()) { showError("Workspace name is required"); return; }
    setSaving(true);
    try {
      await apiRequest("PATCH", `/api/workspaces/${workspace._id}`, { name: name.trim(), description: description.trim() });
      showSuccess("Workspace updated");
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
    } catch (e: any) {
      showError(e.message || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          General
          <Badge variant="outline">{workspace.type}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="ws-name">Workspace Name</Label>
          <Input id="ws-name" value={name} onChange={e => setName(e.target.value)} placeholder="My Kitchen Team" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ws-desc">Description</Label>
          <Textarea id="ws-desc" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description..." rows={2} />
        </div>
        {workspace.subscriptionStatus && (
          <div className="text-sm text-muted-foreground">
            Subscription: <Badge variant={workspace.subscriptionStatus === "active" ? "default" : "secondary"}>{workspace.subscriptionStatus}</Badge>
            {workspace.maxMembers && <span className="ml-2">· Max {workspace.maxMembers} members</span>}
          </div>
        )}
        <Button onClick={handleSave} disabled={!isDirty || saving}>
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          Save Changes
        </Button>
      </CardContent>
    </Card>
  );
}
